import { LocationAutoCompleteWithSuggestion, type Suggestion } from "@apac/ui";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { Funnel, type PlatformComponentProps } from "@b2bportal/core-types";
import type { PlaceId } from "@b2bportal/ground-api";
import {
  PremierDestinationQuickPicks,
  type PromotedDestinationCity,
  usePremierDestinationPromotion,
} from "@hsbc/portal";
import {
  type CarLocationSuggestion,
  useCarLocationAutocomplete,
} from "@tenant/cars";
import { Icon, type LocationAutocompleteProps } from "@tenant/ui";
import clsx from "clsx";
import { fetchLocations } from "./fetchLocations";
import styles from "./styles.module.scss";

export type CarLocationAutocompleteProps = Omit<
  LocationAutocompleteProps<CarLocationSuggestion>,
  "fetch" | "groupBy" | "getOptionLabel" | "sortOptions"
> & {
  value?: CarLocationSuggestion;
} & PlatformComponentProps;

export const CarLocationAutocomplete = (
  props: CarLocationAutocompleteProps
) => {
  const [block] = useModuleBEM(styles, "CarLocationAutocomplete");
  const { context, handlers } = useCarLocationAutocomplete(props);

  const {
    activePremierDestinations: { cars: activePremierDestinations },
    enabledPromotions: { cars: enablePremierDestination },
  } = usePremierDestinationPromotion();

  const onPremierDestinationSelect = (city: PromotedDestinationCity) => {
    const selectedCity = {
      label: city.label,
      id: {
        Id: "Grounds",
        groundSelection: {
          GroundSelection: "Place",
          placeId: city.placeId,
          placeTypes: ["geocode"],
          searchTerm: city.label,
        },
      },
      trackingPropertiesV2: {},
    };

    props.onChange(selectedCity as CarLocationSuggestion);
  };

  return (
    <div className={clsx(block, props.className)}>
      <LocationAutoCompleteWithSuggestion<PlaceId>
        {...props}
        id="cars-location-selection"
        value={context.value}
        onChange={handlers.onChange}
        fetch={fetchLocations}
        getOptionLabel={getOptionLabel}
        getOptionIcon={getOptionIcon}
        funnel={Funnel.Ground}
        noDialog
        renderSuggestions={() => (
          <>
            {enablePremierDestination && (
              <PremierDestinationQuickPicks
                funnel={Funnel.Ground}
                activePremierDestinations={activePremierDestinations}
                onSelect={onPremierDestinationSelect}
              />
            )}
          </>
        )}
      />
    </div>
  );
};

const getOptionLabel = (value1?: Suggestion<PlaceId>) => value1?.label ?? "";

const getOptionIcon = () => <Icon iconName={IconNameEnum.location} />;
